import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/waseeny-logo.png';
import footerLogo from '../../assets/Waseeny Logo Final Black BG.png';
import qrCode from '../../assets/qr-download.png';
import defaultRestoImage from '../../assets/default-resto-image.png';
import useHomeData from "../../Hook/useHomeData";
import { getCatalog, restaurantGroupTagIds } from "../../services/catalogAPI";
import AdminBanners from "./AdminBanners";
import { getSessionToken } from "../../utils/authSession";
import { getCurrentLocation, getAddressFromCoords, saveDeliveryLocation } from '../../utils/locationHelper';
import useSearch from '../../Hook/useSearch';
import { useTranslate } from '../../Hook/useTranslate';
import { useLang } from '../../context/LanguageContext';
import useDynamicTranslation from "../../Hook/useDynamicTranslation";
import { getAbsoluteImageUrl } from '../../utils/imageUrl';
import LocationPickerModal from '../Common/LocationPickerModal';
import FavoriteButton from '../Common/FavoriteButton';
import RestaurantFiltersModal, { DEFAULT_RESTAURANT_FILTERS } from './RestaurantFiltersModal';
import { getDeliveryInfo } from '../../utils/deliveryUtils';
import { getCurrencyFromPhone } from '../../utils/phone';
import NotificationBell from '../Common/NotificationBell';
import { HomeSkeleton } from '../Common/PageSkeleton';
import FormBanner from '../Common/FormBanner';
import { MapPin, Clock, Star, Zap, Check, Tag, Utensils } from 'lucide-react';
import './Home.css';
import './HomePremium.css';

const Home = ({ isAuthenticated, setIsAuthenticated }) => {
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 51.4994, lng: 7.0833 });
  const [userLocationData, setUserLocationData] = useState(() => {
    const stored = localStorage.getItem('userFullAddress');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return {
          lat: parsed.coordinates?.[1] || 51.4994,
          lng: parsed.coordinates?.[0] || 7.0833,
          city: parsed.city || "Bochum"
        };
      } catch (e) { }
    }
    return { lat: 51.4994, lng: 7.0833, city: "Bochum" };
  });

  useEffect(() => {
    const stored = localStorage.getItem('userFullAddress');
    if (!stored) {
      setIsLocationModalOpen(true);
    }
  }, []);

  const { data: homeData, loading, error } = useHomeData(
    userLocationData?.lat,
    userLocationData?.lng,
    userLocationData?.city
  );
  const { lang, setLang } = useLang(); // Use useLang hook instead of useContext
  const languages = [{ code: 'en', label: 'English' }, { code: 'de', label: 'Deutsch' }, { code: 'ar', label: 'العربية' }];
  const [query, setQuery] = useState('');
  const [detectedLocation, setDetectedLocation] = useState(localStorage.getItem('userLocation') || null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [locationBanner, setLocationBanner] = useState({ type: '', text: '' });
  const { suggestions } = useSearch(query);
  const navigate = useNavigate();

  const [groupsData, setGroupsData] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupTag, setSelectedGroupTag] = useState(null);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const catalog = await getCatalog();
        setGroupsData(catalog.groups);
      } catch (err) {
      }
    };
    fetchGroups();
  }, []);

  const handleDetectLocation = async () => {
    if (isDetecting) return; // Prevent double clicks
    try {
      setIsDetecting(true);
      const coords = await getCurrentLocation();
      const addressData = await getAddressFromCoords(coords.lat, coords.lng);

      const shortAddress = addressData.formattedAddress.split(',')[0] + ', ' + addressData.city;
      setDetectedLocation(shortAddress);
      setUserLocationData({
        lat: coords.lat,
        lng: coords.lng,
        city: addressData.city
      });

      // Save full address data for later use in checkout
      localStorage.setItem('userLocation', shortAddress);
      localStorage.setItem('userFullAddress', JSON.stringify(addressData));

      setLocationBanner({
        type: 'success',
        text: `${t("locationDetected")}: ${addressData.formattedAddress}`
      });
    } catch (err) {
      console.error("Location detection error:", err);
      setLocationBanner({
        type: 'error',
        text: t("locationError") || "Failed to detect location. Please allow location access."
      });
    } finally {
      setIsDetecting(false);
    }
  };

  const updateLocationData = async (lat, lng, formattedAddr = null, known = {}) => {
    try {
      const saved = await saveDeliveryLocation(lat, lng, formattedAddr, known);
      setDetectedLocation(saved.shortAddress);
      setUserLocationData({ lat, lng, city: saved.city });
      setMapCenter({ lat, lng });
    } catch (err) {
      console.error("Error updating location:", err);
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleManualLocationChange = () => {

    // Initialize map center to current user location if available
    if (userLocationData?.lat && userLocationData?.lng) {
      setMapCenter({ lat: userLocationData.lat, lng: userLocationData.lng });
    }
    setIsLocationModalOpen(true);
  };

  const categoryScrollRef = useRef(null);
  const brandsScrollRef = useRef(null);
  const langContainerRef = useRef(null);
  const [langOpen, setLangOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [, setHoveredRestaurant] = useState(null);
  const [activeTab, setActiveTab] = useState('online'); // Define activeTab state
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterState, setFilterState] = useState(DEFAULT_RESTAURANT_FILTERS);
  const [imageErrors, setImageErrors] = useState({});
  const t = useTranslate();
  const categories = useMemo(() => homeData?.categories || [], [homeData?.categories]);
  const restaurants = useMemo(
    () => homeData?.sections?.recommendedForYou || [],
    [homeData?.sections?.recommendedForYou]
  );
  const popularRestaurants = useMemo(
    () => homeData?.sections?.popularRestaurants || [],
    [homeData?.sections?.popularRestaurants]
  );

  const safeText = (data) => {
    if (!data) return "";
    if (typeof data === "object") {
      return (data.en || Object.values(data)[0] || "").toString().toLowerCase();
    }
    return String(data).toLowerCase();
  };

  const applyFiltersAndSort = (rests) => {
    let result = [...rests];

    const getEstimatedMinutes = (r) => {
      const info = getDeliveryInfo(r, userLocationData?.lat, userLocationData?.lng, r.distanceKm);
      if (info?.timeLabel) {
        const match = info.timeLabel.match(/\d+/);
        if (match) return parseInt(match[0], 10);
      }
      return parseInt(r.deliveryTime, 10) || 30;
    };

    if (filterState.fastDelivery) result = result.filter(r => getEstimatedMinutes(r) <= 30 || safeText(r.deliveryLabel).includes('30'));
    if (filterState.rating === '4.5') result = result.filter(r => (r.rating?.average >= 4.5) || (r.rating >= 4.5));
    if (filterState.rating === '4.0') result = result.filter(r => (r.rating?.average >= 4.0) || (r.rating >= 4.0));
    if (filterState.offers) {
      result = result.filter(r => {
        if (r.isFreeDelivery || r.offer || r.hasOffer) return true;
        const tagList = Array.isArray(r.tags) ? r.tags : (r.tag ? [r.tag] : []);
        return tagList.some(t => {
          const n = safeText(typeof t === 'string' ? t : t?.name);
          return n.includes('offer') || n.includes('free') || n.includes('deal') || n.includes('%') || n.includes('discount') || n.includes('promo');
        });
      });
    }
    if (filterState.pureVeg) result = result.filter(r => r.isPureVeg || (r.cuisine || []).some(c => safeText(c) === 'pure veg' || safeText(c) === 'veg'));
    if (filterState.openNow) result = result.filter(r => r.isOpen !== false);

    if (filterState.foodType.length > 0) {
      result = result.filter(r => filterState.foodType.some(type => {
        if (type === 'Veg') return r.isPureVeg || (r.cuisine || []).some(c => safeText(c).includes('veg'));
        if (type === 'Non-Veg') return (r.cuisine || []).some(c => safeText(c).includes('non-veg') || safeText(c).includes('chicken') || safeText(c).includes('meat'));
        if (type === 'Vegan') return (r.cuisine || []).some(c => safeText(c).includes('vegan'));
        return false;
      }));
    }

    if (filterState.maxDistanceKm) {
      result = result.filter(r => {
        const km = getDeliveryInfo(r, userLocationData?.lat, userLocationData?.lng, r.distanceKm)?.distanceKm;
        return km !== null && km !== undefined && km <= filterState.maxDistanceKm;
      });
    }

    if (filterState.cuisines?.length > 0) {
      const wanted = filterState.cuisines.map(c => safeText(c));
      result = result.filter(r => (r.cuisine || []).some(c => wanted.includes(safeText(c))));
    }

    // Dish tags: exact ids on the restaurant or on its dishes. Group pills match any tag of the group.
    const wantedTags = [
      ...(filterState.groupTags || []),
      ...(selectedGroupTag ? [String(selectedGroupTag._id || selectedGroupTag)] : []),
    ];
    const groupTagIds = !selectedGroupTag && selectedGroup ? (selectedGroup.tags || []).map(t => String(t._id || t)) : [];
    if (wantedTags.length > 0) {
      result = result.filter(r => {
        const ids = restaurantGroupTagIds(r);
        return wantedTags.every(id => ids.has(id));
      });
    }
    if (groupTagIds.length > 0) {
      result = result.filter(r => {
        const ids = restaurantGroupTagIds(r);
        return groupTagIds.some(id => ids.has(id));
      });
    }

    if (filterState.sortBy === 'Delivery Time') {
      result.sort((a, b) => getEstimatedMinutes(a) - getEstimatedMinutes(b));
    } else if (filterState.sortBy === 'Rating') {
      const getRating = r => r.rating?.average || r.rating || 0;
      result.sort((a, b) => getRating(b) - getRating(a));
    } else if (filterState.sortBy === 'Cost Low to High') {
      result.sort((a, b) => (a.costForTwo || 9999) - (b.costForTwo || 9999));
    } else if (filterState.sortBy === 'Cost High to Low') {
      result.sort((a, b) => (b.costForTwo || 0) - (a.costForTwo || 0));
    }

    return result;
  };

  const filteredRestaurants = useMemo(() => applyFiltersAndSort(restaurants), [restaurants, filterState, selectedGroup, selectedGroupTag]);
  const filteredPopularRestaurants = useMemo(() => applyFiltersAndSort(popularRestaurants), [popularRestaurants, filterState, selectedGroup, selectedGroupTag]);

  const brandRestaurants = useMemo(() => {
    const serverBrandRests = homeData?.sections?.brandRestaurants;
    if (Array.isArray(serverBrandRests) && serverBrandRests.length > 0) {
      return applyFiltersAndSort(serverBrandRests);
    }

    const sections = homeData?.sections || {};
    const allRests = [
      ...(sections.popularRestaurants || []),
      ...(sections.recommendedForYou || []),
      ...(sections.exploreRestaurants || []),
      ...(sections.fastDelivery || []),
      ...(sections.newOnPlatform || [])
    ];
    const uniqueMap = new Map();
    allRests.forEach(r => {
      const b = r?.brand;
      const hasBrand = Boolean(b && typeof b === 'string' && b.trim() !== '' && b.toLowerCase() !== 'none' && b.toLowerCase() !== 'select');
      if (r && hasBrand) {
        const id = r._id || r.id;
        if (id && !uniqueMap.has(id)) {
          uniqueMap.set(id, r);
        }
      }
    });

    const clientBrandRests = Array.from(uniqueMap.values());
    if (clientBrandRests.length > 0) {
      return applyFiltersAndSort(clientBrandRests);
    }

    return filteredPopularRestaurants || [];
  }, [homeData?.sections, filteredPopularRestaurants, filterState, selectedGroup, selectedGroupTag]);

  const extractText = (data) => {
    if (!data) return "";

    if (typeof data === "object") {
      return data.en || Object.values(data)[0] || "";
    }

    return String(data);
  };

  const allTexts = useMemo(() => [
    ...categories.map(c => extractText(c.name)),
    ...restaurants.map(r => extractText(r.name)),
    ...restaurants.map(r => extractText(r.tag)),
    ...restaurants.flatMap(r => (r.cuisine || []).map(c => extractText(c))),
    ...popularRestaurants.map(r => extractText(r.name))
  ], [categories, restaurants, popularRestaurants]);
  const { getText } = useDynamicTranslation(allTexts);
  // Logic to translate text using backend API route

  const handleSearch = () => {
    if (!query.trim()) {
      setSearchError(t("enterSearchTerm"));
      return;
    }
    setSearchError('');
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const handleSelect = (item) => {
    const nameText = getText(item.name || item.text || item);
    setQuery(nameText);

    // Automatically navigate to restaurant page
    let targetRestId = item.restaurantId || item.restaurant || item.id || item._id;
    if (targetRestId && typeof targetRestId === 'object') {
      targetRestId = targetRestId._id || targetRestId.id || targetRestId;
    }

    if (targetRestId) {
      navigate(`/restaurant/${targetRestId}`);
    } else {
      navigate(`/search?q=${encodeURIComponent(nameText)}`);
    }
  };

  const handleCategoryClick = (categoryName) => {
    // Set query and navigate to search results
    setQuery(categoryName);
    navigate(`/search?q=${encodeURIComponent(categoryName)}`);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langContainerRef.current && !langContainerRef.current.contains(event.target)) {
        setLangOpen(false);
      }
    };
    if (langOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [langOpen, langContainerRef]);

  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (isAuthenticated) {
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/web-user/profile`, {
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${getSessionToken() || ''}` }
          });
          if (!res.ok) return;
          const data = await res.json();
          setUserData(data);
        } catch (err) { }
      }
    };
    fetchProfile();
  }, [isAuthenticated]);

  const handleLogout = () => {
    localStorage.clear();
    setIsAuthenticated(false);
    navigate('/');
    window.location.reload();
  };

  const handleLanguageChange = (newLangCode) => {
    setLang(newLangCode); // Update context and localStorage
    setLangOpen(false);
  };



  const scrollCategories = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 400;
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const markImageError = (key) => {
    setImageErrors((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  const handleImageError = (event, key) => {
    event.currentTarget.src = defaultRestoImage;
    markImageError(key);
  };

  const getRestaurantImageKey = (rest, sectionKey, index) =>
    `${sectionKey}-${rest?._id || rest?.id || index}`;

  const getImageCandidates = (value) => {
    if (!value) return [];

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? [trimmed] : [];
    }

    if (Array.isArray(value)) {
      return value.flatMap(getImageCandidates);
    }

    if (typeof value === 'object') {
      const directValues = [value.url, value.src, value.path, value.image, value.secure_url, value.thumbnail, value.filename]
        .filter((item) => typeof item === 'string' && item.trim())
        .map((item) => item.trim());

      if (directValues.length > 0) return directValues;
      if (Array.isArray(value.images)) return value.images.flatMap(getImageCandidates);
    }

    return [];
  };

  const getRestaurantImageSource = (rest) => {
    const candidateGroups = [
      rest?.image,
      rest?.bannerImage,
      rest?.coverImage,
      rest?.restaurantImages,
      rest?.images,
      rest?.gallery,
      rest?.photos,
      rest?.photo,
      rest?.restaurant?.image,
      rest?.restaurant?.bannerImage,
    ];

    for (const group of candidateGroups) {
      const found = getImageCandidates(group);
      if (found.length > 0) return found[0];
    }

    return "";
  };

  const renderRestaurantCard = (rest, index, sectionKey) => {
    const imageKey = getRestaurantImageKey(rest, sectionKey, index);
    const imageSrc = getAbsoluteImageUrl(getRestaurantImageSource(rest)) || defaultRestoImage;
    const hasImage = Boolean(imageSrc) && !imageErrors[imageKey];
    const dynamicTags = Array.isArray(rest?.tags) ? rest.tags.filter(t => t && typeof t === 'object' && t.name) : [];
    const primaryTag = dynamicTags[0];
    const secondaryTag = dynamicTags[1];

    const rawOffer = rest?.offer || rest?.discount || rest?.promotion;
    const offerText = primaryTag ? primaryTag.name : (rawOffer ? getText(rawOffer).trim() : '');
    const offerBgColor = primaryTag?.color || '#ed2026';

    const rawTag = rest?.tag;
    const tagText = secondaryTag ? secondaryTag.name : (rawTag ? getText(rawTag).trim() : '');
    const tagBgColor = secondaryTag?.color || undefined;

    const cuisineText = (rest?.cuisine || [])
      .map((item) => getText(item).trim())
      .filter(Boolean)
      .join(", ");
    const locationText = [getText(rest?.area), getText(rest?.city)].filter(Boolean).join(", ");
    const restaurantName = getText(rest?.name);
    const ratingValue = typeof rest?.rating === 'object'
      ? (rest?.rating?.average || '0.0')
      : (rest?.rating || '0.0');
    const fallbackInitial = getText(rest?.name).trim().charAt(0).toUpperCase() || 'R';

    // This ensures restaurants appear 'nearby' during development/demo
    const restWithCoords = {
      ...rest,
      lat: rest.lat || rest.latitude || rest.location?.coordinates?.[1] || (userLocationData?.lat + (index % 2 === 0 ? 0.008 : 0.015)),
      lng: rest.lng || rest.longitude || rest.location?.coordinates?.[0] || (userLocationData?.lng + (index % 2 === 0 ? 0.008 : -0.012))
    };

    // Smart delivery info using backend distance if available, otherwise calculate
    const deliveryInfo = getDeliveryInfo(
      restWithCoords,
      userLocationData?.lat,
      userLocationData?.lng,
      rest.distanceKm,
      getCurrencyFromPhone(rest.contactNumber)
    );

    const translatedTimeLabel = deliveryInfo.timeLabel
      ? deliveryInfo.timeLabel.includes('mins')
        ? deliveryInfo.timeLabel.replace('mins', t('min'))
        : deliveryInfo.timeLabel === 'Fast delivery'
          ? t('fastDelivery')
          : deliveryInfo.timeLabel
      : '';

    const translatedDeliveryFee = deliveryInfo.deliveryFee
      ? deliveryInfo.deliveryFee === 'Free Delivery'
        ? t('freeDelivery')
        : deliveryInfo.deliveryFee.includes('delivery')
          ? deliveryInfo.deliveryFee.replace('delivery', t('delivery'))
          : deliveryInfo.deliveryFee
      : '';

    return (
      <article
        key={`${sectionKey}-${rest?._id || rest?.id || index}`}
        className="restaurant-card"
        onClick={() => navigate(`/restaurant/${rest._id}`)}
        onMouseEnter={() => setHoveredRestaurant(rest.id)}
        onMouseLeave={() => setHoveredRestaurant(null)}
        style={{ animationDelay: `${index * 0.05}s` }}
      >
        <div className="restaurant-image-container">
          <img
            src={hasImage ? imageSrc : defaultRestoImage}
            alt={restaurantName}
            className="restaurant-image"
            loading="lazy"
            onError={(event) => handleImageError(event, imageKey)}
          />
          {offerText && (
            <div
              className="discount-badge"
              style={{
                background: offerBgColor || "var(--primary-gradient)",
                backgroundColor: offerBgColor || "var(--primary-color)",
                backgroundImage: "none",
                color: "#ffffff",
                position: "absolute",
                top: "12px",
                left: "12px",
                padding: "4px 10px",
                borderRadius: "8px",
                fontSize: "11px",
                fontWeight: "800",
                textTransform: "uppercase",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
              }}
            >
              {primaryTag?.image && (
                <img src={getAbsoluteImageUrl(primaryTag.image)} alt="" className="w-3.5 h-3.5 object-contain inline-block mr-1" />
              )}
              {offerText}
            </div>
          )}
          <FavoriteButton kind="restaurant" id={rest._id || rest.id} />
        </div>

        <div className="restaurant-info">
          <div className="restaurant-header-row">
            <h3 className="restaurant-name">{restaurantName}</h3>
            <div className="rating-badge-pill">
              <span>★</span>
              <span>{ratingValue}</span>
            </div>
          </div>

          {(cuisineText || locationText) && (
            <p className="restaurant-category">
              {cuisineText || locationText}
            </p>
          )}

          {/* Smart Delivery Info Row */}
          <div className="delivery-info-row">
            <span className="delivery-time-badge" style={{ display: 'inline-flex', alignItems: 'center' }}>
              <Clock size={12} style={{ marginRight: 4 }} />
              {translatedTimeLabel}
            </span>
            {deliveryInfo.distLabel && (
              <>
                <span className="delivery-dot">•</span>
                <span>{deliveryInfo.distLabel}</span>
              </>
            )}
            <span className="delivery-dot">•</span>
            <span className={`delivery-fee ${deliveryInfo.deliveryFee === 'Free Delivery' ? 'free' : 'paid'}`}>
              {translatedDeliveryFee}
            </span>
          </div>
        </div>
      </article>
    );
  };

  const renderNoRestaurantState = () => (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        padding: "24px 0 8px"
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "520px",
          padding: "28px 22px",
          borderRadius: "20px",
          border: "1px solid #f1dfd2",
          background: "linear-gradient(135deg, #fffaf6 0%, #fff3eb 100%)",
          boxShadow: "0 12px 30px rgba(197, 25, 45, 0.08)",
          textAlign: "center"
        }}
      >
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            margin: "0 auto 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.06)"
          }}
        >
          <MapPin size={26} color="#E31E24" />
        </div>
        <h3
          style={{
            margin: "0 0 8px",
            fontSize: "clamp(18px, 2vw, 22px)",
            fontWeight: "700",
            color: "#1f2937"
          }}
        >
          {t("noRestaurantsNearby")}
        </h3>
        <p
          style={{
            margin: 0,
            fontSize: "clamp(13px, 1.8vw, 15px)",
            lineHeight: "1.6",
            color: "#6b7280"
          }}
        >
          {t("noRestaurantsNearbyDesc")}
        </p>
      </div>
    </div>
  );

  if (loading) return <HomeSkeleton />;
  if (error) return <p>{t("error")}: {error.message || error}</p>;

  return (
    <div className="home-container">



      {/* ========== MODERN STICKY HEADER ========== */}
      <header className="home-header-v2">
        <div className="header-content-v2">
          <div className="header-left-v2">
            <img src={logo} alt="Waseeny" className="logo-v2" onClick={() => navigate('/')} />
            <div className="location-picker-v2" onClick={handleManualLocationChange}>
              <div className="location-icon-wrapper">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <div className="location-text-v2">
                <span className="location-city">{userLocationData?.city || "Bochum"}</span>
                <span className="location-address">{detectedLocation || "Hotner Str., Bochum, Germany"}</span>
              </div>
              <svg className="chevron-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
          </div>

          <div className="header-right-v2">
            <div className="header-search-v2" style={{ position: 'relative' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder={t("searchPlaceholder")}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSearchError(''); }}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              {searchError && (
                <div
                  role="alert"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '6px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#b91c1c',
                    fontSize: '12px',
                    zIndex: 100001
                  }}
                >
                  {searchError}
                </div>
              )}
              {query.trim().length >= 2 && suggestions && suggestions.filter(item => item.type === 'dish').length > 0 && (
                <div className="search-suggestions-dropdown" style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '8px',
                  backgroundColor: '#ffffff', borderRadius: '16px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                  maxHeight: '360px', overflowY: 'auto', zIndex: 100000, border: '1px solid #f1f5f9',
                  padding: '12px 0', textAlign: 'left', animation: 'fadeIn 0.2s ease'
                }}>
                  {suggestions.filter(item => item.type === 'dish').map((item, idx, arr) => {
                    const rName = item.restaurantName && typeof item.restaurantName === 'object' ? (item.restaurantName.en || Object.values(item.restaurantName)[0]) : item.restaurantName;
                    return (
                      <div
                        key={item.id || item._id || idx}
                        onClick={() => handleSelect(item)}
                        style={{
                          padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '14px',
                          cursor: 'pointer', transition: 'background-color 0.2s ease', borderBottom: idx < arr.length - 1 ? '1px solid #f8fafc' : 'none'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <img
                          src={getAbsoluteImageUrl(item.image) || defaultRestoImage}
                          alt=""
                          style={{ width: '42px', height: '42px', borderRadius: '10px', objectFit: 'cover' }}
                          onError={(e) => { e.target.src = defaultRestoImage; }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '700', fontSize: '15px', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.name || item.text || ""}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b', textTransform: 'capitalize', marginTop: '2px' }}>
                            🍴 {t("dish")} {rName ? `• ${rName}` : ''}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="header-actions-v2">
              <div ref={langContainerRef} className="lang-container-v2">
                <button onClick={() => setLangOpen(!langOpen)} className="lang-btn-v2">
                  {lang.toUpperCase()}
                </button>
                {langOpen && (
                  <div className="lang-dropdown-v2">
                    {languages.map((l) => (
                      <div key={l.code} onClick={() => handleLanguageChange(l.code)} className={`lang-opt-v2 ${lang === l.code ? 'active' : ''}`}>
                        {l.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {isAuthenticated && <NotificationBell />}

              {isAuthenticated ? (
                <div className="profile-menu-v2">
                  <button className="profile-trigger-v2" onClick={() => setDropdownOpen(!dropdownOpen)}>
                    <div className="profile-initial">{userData?.name?.[0] || 'U'}</div>
                  </button>
                  {dropdownOpen && (
                    <div className="profile-dropdown-v2">
                      <div className="dropdown-item" onClick={() => navigate('/profile')}>{t("Profile")}</div>
                      <div className="dropdown-item" onClick={() => navigate('/order-tracking')}>{t("orders")}</div>
                      <div className="dropdown-item logout" onClick={handleLogout}>{t("logout")}</div>
                    </div>
                  )}
                </div>
              ) : (
                <button className="signin-btn-v2" onClick={() => navigate('/login')}>{t("signIn")}</button>
              )}
            </div>
          </div>
        </div>
      </header>

      {locationBanner.text && (
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 16px' }}>
          <FormBanner
            message={locationBanner.text}
            type={locationBanner.type || 'error'}
            onDismiss={() => setLocationBanner({ type: '', text: '' })}
          />
        </div>
      )}

      {/* ========== MODERN HERO / PROMO SECTION ========== */}
      <section className="hero-section-v2">
        <div className="hero-slider-v2">
          <div className="hero-slide-v2" style={{ backgroundImage: `url('https://img.freepik.com/free-photo/top-view-table-full-food_23-2149209251.jpg?w=2000')` }}>
            <div className="hero-overlay-v2" />
            <div className="hero-content-v2">
              <span className="hero-badge-v2">{t("exclusiveDeals")}</span>
              <h1 className="hero-title-v2">{t("food")}</h1>
              <p className="hero-subtitle-v2">{t("orderFoodFromRestaurants")}</p>
              <button className="hero-cta-v2" onClick={() => scrollToSection('best-outlets')}>{t("exploreNow")}</button>
            </div>
          </div>
        </div>
      </section>

      <AdminBanners city={userLocationData?.city && userLocationData.city !== "Select Location" ? userLocationData.city : undefined} />

      <main className="main-content-v3">
        {/* ========== GROUPS & GROUP TAGS SECTION ========== */}
        {groupsData && groupsData.length > 0 && (
          <section className="groups-section-v2">
            <div className="section-header-v2" style={{ marginBottom: '14px' }}>
              <h2 className="section-title-v2">{t("exploreCollections")}</h2>
            </div>
            
            {/* Top Groups Pills */}
            <div className="group-pills-wrapper">
              <button
                className={`group-pill-btn ${!selectedGroup ? 'active' : ''}`}
                onClick={() => { setSelectedGroup(null); setSelectedGroupTag(null); }}
              >
                {t("all") || "All Groups"}
              </button>
              {groupsData.map((group) => (
                <button
                  key={group._id}
                  className={`group-pill-btn ${selectedGroup?._id === group._id ? 'active' : ''}`}
                  onClick={() => {
                    if (selectedGroup?._id === group._id) {
                      setSelectedGroup(null);
                      setSelectedGroupTag(null);
                    } else {
                      setSelectedGroup(group);
                      setSelectedGroupTag(null);
                    }
                  }}
                >
                  {group.image && (
                    <img
                      src={getAbsoluteImageUrl(group.image)}
                      alt=""
                      style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  {group.name}
                </button>
              ))}
            </div>

            {/* Sub Group Tags Chips */}
            {selectedGroup && selectedGroup.tags && selectedGroup.tags.length > 0 && (
              <div className="group-tag-subbar">
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>
                  Tags in {selectedGroup.name}:
                </span>
                <button
                  className={`group-tag-chip-btn ${!selectedGroupTag ? 'active' : ''}`}
                  onClick={() => setSelectedGroupTag(null)}
                >
                  {t("all") || "All"}
                </button>
                {selectedGroup.tags.map((tag) => (
                  <button
                    key={tag._id}
                    className={`group-tag-chip-btn ${selectedGroupTag?._id === tag._id ? 'active' : ''}`}
                    onClick={() => {
                      if (selectedGroupTag?._id === tag._id) {
                        setSelectedGroupTag(null);
                      } else {
                        setSelectedGroupTag(tag);
                      }
                    }}
                  >
                    #{tag.name}
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ========== WHAT'S ON YOUR MIND (CATEGORIES) ========== */}
        <section className="offers-section-v2">
          <div className="section-header-v2">
            <h2 className="section-title-v2">{t("todaysOffers") || "Today's Offers"}</h2>
          </div>
          <div className="offers-carousel-v2">
            {(filteredPopularRestaurants || []).slice(0, 5).map((rest, idx) => {
              const dynamicTags = Array.isArray(rest?.tags) ? rest.tags.filter(t => t && typeof t === 'object' && t.name) : [];
              const primaryTag = dynamicTags[0];
              const offerText = primaryTag ? primaryTag.name : (rest?.offer || rest?.discount || rest?.promotion || '');
              const offerImage = getAbsoluteImageUrl(getRestaurantImageSource(rest)) || defaultRestoImage;
              return (
                <div key={`offer-${idx}`} className="offer-card-v2" onClick={() => navigate(`/restaurant/${rest._id || rest.id}`)}>
                  <img
                    src={offerImage}
                    alt={getText(rest.name)}
                    className="offer-card-img-v2"
                    onError={(event) => {
                      event.currentTarget.src = defaultRestoImage;
                    }}
                  />
                  <div className="offer-card-overlay-v2" />
                  <div className="offer-card-content-v2">
                    {offerText && (
                      <span
                        className="offer-card-badge-v2"
                        style={primaryTag?.color ? { backgroundColor: primaryTag.color, background: primaryTag.color, backgroundImage: "none", color: "#ffffff" } : undefined}
                      >
                        {offerText}
                      </span>
                    )}
                    <h3 className="offer-card-title-v2">{getText(rest.name)}</h3>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="category-section-v2">
          <div className="section-header-v2">
            <h2 className="section-title-v2">{t("whatsOnMind") || "What's on your mind?"}</h2>
            <div className="scroll-controls-v2">
              <button className="scroll-btn-v2" onClick={() => scrollCategories('left')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <button className="scroll-btn-v2" onClick={() => scrollCategories('right')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>

          </div>
          <div className="category-carousel-v2" ref={categoryScrollRef}>
            {categories.map((cat, idx) => {
              const catName = getText(cat.name) || "";
              const getCategoryFallbackImage = (name) => {
                const n = (name || "").toLowerCase();
                if (n.includes('pizza')) return 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=200&h=200&fit=crop';
                if (n.includes('burger')) return 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=200&h=200&fit=crop';
                if (n.includes('pasta')) return 'https://images.unsplash.com/photo-1551183053-bf91a1d81141?w=200&h=200&fit=crop';
                if (n.includes('sweet') || n.includes('dessert') || n.includes('cake')) return 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=200&h=200&fit=crop';
                if (n.includes('salad')) return 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=200&h=200&fit=crop';
                if (n.includes('rice') || n.includes('biryani')) return 'https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=200&h=200&fit=crop';
                if (n.includes('ice cream')) return 'https://images.unsplash.com/photo-1497034825429-c343d7c6a68f?w=200&h=200&fit=crop';
                if (n.includes('sandwich')) return 'https://images.unsplash.com/photo-1528735602780-2552fd46c7af?w=200&h=200&fit=crop';
                return 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=200&h=200&fit=crop';
              };
              const imageSrc = cat.image ? getAbsoluteImageUrl(cat.image) : getCategoryFallbackImage(catName);
              return (
                <div key={idx} className="cat-card-v2" onClick={() => handleCategoryClick(cat.name)}>
                  <div className="cat-img-wrapper-v2">
                    <img
                      src={imageSrc}
                      alt={catName}
                      className="cat-img-v2"
                      onError={(event) => {
                        event.currentTarget.src = getCategoryFallbackImage(catName);
                      }}
                    />
                  </div>
                  <span className="cat-name-v2">{catName}</span>
                </div>
              );
            })}
          </div>

        </section>


        {/* ========== TOP BRANDS ========== */}
        {brandRestaurants && brandRestaurants.length > 0 && (
          <section className="brands-section-v2">
            <div className="section-header-v2">
              <h2 className="section-title-v2">{t("topBrandsIn") || "Top Brands in"} {userLocationData?.city === "Select Location" ? (t("yourArea") || "Bochum") : (userLocationData?.city || "Bochum")}</h2>
              <div className="scroll-controls-v2">
                <button className="scroll-btn-v2" onClick={() => brandsScrollRef.current?.scrollBy({ left: -300, behavior: 'smooth' })}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
                <button className="scroll-btn-v2" onClick={() => brandsScrollRef.current?.scrollBy({ left: 300, behavior: 'smooth' })}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </div>
            </div>
            <div className="brands-carousel-v2" ref={brandsScrollRef}>
              {brandRestaurants.slice(0, 10).map((rest, idx) => {
                const imageKey = `brand-${rest._id || idx}`;
                const imageSrc = getAbsoluteImageUrl(getRestaurantImageSource(rest)) || defaultRestoImage;
                const hasImage = !imageErrors[imageKey];

                return (
                  <div key={idx} className="brand-card-v2" onClick={() => navigate(`/restaurant/${rest._id || rest.id}`)}>
                    <div className="brand-logo-v2">
                      <img
                        src={hasImage ? imageSrc : defaultRestoImage}
                        alt={getText(rest.name)}
                        onError={(event) => handleImageError(event, imageKey)}
                      />
                    </div>
                    <span className="brand-name-v2">{getText(rest.name)}</span>
                    <span className="brand-time-v2">{rest.deliveryTime || '25'} {t("min") || "min"}</span>
                  </div>
                );
              })}
            </div>

          </section>
        )}

        {/* ========== WHY CHOOSE WASEENY? ========== */}
        <section className="why-choose-us-section">
          <div className="section-header-v2">
            <h2 className="section-title-v2">{t("whyChooseWaseeny") || "Why Choose Waseeny?"}</h2>
          </div>
          <div className="why-choose-us-grid">
            <div className="why-card">
              <div className="why-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path></svg>
              </div>
              <h3 className="why-title">{t("fastestDelivery") || "Fastest Delivery"}</h3>
              <p className="why-description">{t("fastestDeliveryDesc") || "Hot and fresh meals delivered straight to your door in 30 minutes or less."}</p>
            </div>
            <div className="why-card">
              <div className="why-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
              </div>
              <h3 className="why-title">{t("securePayments") || "Secure Payments"}</h3>
              <p className="why-description">{t("securePaymentsDesc") || "Pay safely and easily with Stripe, Apple Pay, PayPal, or Cash on Delivery."}</p>
            </div>
            <div className="why-card">
              <div className="why-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              </div>
              <h3 className="why-title">{t("qualityFood") || "Top Food Quality"}</h3>
              <p className="why-description">{t("qualityFoodDesc") || "Curated top-rated restaurants and local kitchen favorites in your neighborhood."}</p>
            </div>
          </div>
        </section>

        <div id="best-outlets" />

        <div className="filter-sort-bar">
          <div className="filter-bar-left">
            <button
              className={`filter-bar-btn ${Object.values(filterState).some(v => v && v !== 'Relevance' && (Array.isArray(v) ? v.length > 0 : true)) ? 'has-filters' : ''}`}
              onClick={() => setIsFilterModalOpen(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="8" y1="12" x2="16" y2="12" />
                <line x1="11" y1="18" x2="13" y2="18" />
              </svg>
              {t("filters") || "Filters"}
              {(() => {
                const count = [
                  filterState.fastDelivery,
                  filterState.rating,
                  filterState.offers,
                  filterState.pureVeg,
                  filterState.openNow,
                  filterState.newRestaurants,
                  filterState.maxDistanceKm,
                  ...(filterState.foodType || []),
                  ...(filterState.costRange || []),
                  ...(filterState.cuisines || []),
                  ...(filterState.groupTags || []),
                ].filter(Boolean).length;
                return count > 0 ? <span className="filter-badge">{count}</span> : null;
              })()}
            </button>

            <div className="filter-quick-chips">
              <button className={`quick-chip ${filterState.fastDelivery ? 'active' : ''}`} onClick={() => setFilterState(p => ({ ...p, fastDelivery: !p.fastDelivery }))}>{t("fastDelivery") || "Fast Delivery"}</button>
              <button className={`quick-chip ${filterState.rating === '4.5' ? 'active' : ''}`} onClick={() => setFilterState(p => ({ ...p, rating: p.rating === '4.5' ? null : '4.5' }))}>4.5+ Rating</button>
              <button className={`quick-chip ${filterState.pureVeg ? 'active' : ''}`} onClick={() => setFilterState(p => ({ ...p, pureVeg: !p.pureVeg }))}>{t("pureVeg") || "Pure Veg"}</button>
              <button className={`quick-chip ${filterState.offers ? 'active' : ''}`} onClick={() => setFilterState(p => ({ ...p, offers: !p.offers }))}>{t("offers") || "Offers"}</button>
              <button className={`quick-chip ${filterState.openNow ? 'active' : ''}`} onClick={() => setFilterState(p => ({ ...p, openNow: !p.openNow }))}>{t("openNow") || "Open Now"}</button>
            </div>
          </div>

          <div className="filter-bar-right">
            <select
              className="sort-select"
              value={filterState.sortBy}
              onChange={(e) => setFilterState(p => ({ ...p, sortBy: e.target.value }))}
            >
              <option value="Relevance">{t("sortRelevance") || "Relevance"}</option>
              <option value="Delivery Time">{t("sortDeliveryTime") || "Delivery Time"}</option>
              <option value="Rating">{t("sortRating") || "Rating"}</option>
              <option value="Cost Low to High">{t("sortCostLowToHigh") || "Cost: Low to High"}</option>
              <option value="Cost High to Low">{t("sortCostHighToLow") || "Cost: High to Low"}</option>
            </select>
          </div>
        </div>

        <section className="listing-section-v2">
          <div className="listing-header-v2">
            <h2 className="section-title-v2">{t("bestFood") || "Restaurants Near You"}</h2>
          </div>

          <div className="restaurant-grid-v2">
            {restaurants.length === 0 ? (
              renderNoRestaurantState()
            ) : filteredRestaurants.length === 0 ? (
              <div className="no-filter-result">
                <Utensils size={36} color="#94a3b8" style={{ marginBottom: '12px' }} />
                <p>{t("noRestaurantsMatchFilters") || "No restaurants match your selected filters."}</p>
                <button onClick={() => setFilterState({ ...DEFAULT_RESTAURANT_FILTERS })}>{t("clearFilters") || "Clear Filters"}</button>
              </div>
            ) : (
              filteredRestaurants.map((rest, index) => renderRestaurantCard(rest, index, 'recommended'))
            )}
          </div>
        </section>

        {/* ========== CUSTOMER REVIEWS ========== */}
        <section className="customer-reviews-section">
          <div className="section-header-v2">
            <h2 className="section-title-v2">{t("whatOurCustomersSay") && t("whatOurCustomersSay") !== "whatOurCustomersSay" ? t("whatOurCustomersSay") : "What Our Customers Say"}</h2>
          </div>
          <div className="reviews-grid">
            <div className="review-card">
              <div className="review-stars" style={{ display: 'flex', gap: '3px', color: '#f59e0b', marginBottom: '12px' }}>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="#f59e0b" color="#f59e0b" />
                ))}
              </div>
              <p className="review-text">"Waseeny is my go-to for late-night cravings. The delivery is always super fast and the food is always fresh and hot!"</p>
              <div className="review-author">
                <div className="review-avatar">AK</div>
                <div className="review-meta">
                  <span className="author-name">Ali Khan</span>
                  <span className="author-status">Verified Customer ✓</span>
                </div>
              </div>
            </div>
            <div className="review-card">
              <div className="review-stars" style={{ display: 'flex', gap: '3px', color: '#f59e0b', marginBottom: '12px' }}>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="#f59e0b" color="#f59e0b" />
                ))}
              </div>
              <p className="review-text">"I love the variety of restaurants and dishes available. The UI is smooth and tracking the rider live is effortless!"</p>
              <div className="review-author">
                <div className="review-avatar">FA</div>
                <div className="review-meta">
                  <span className="author-name">Fatima Ahmed</span>
                  <span className="author-status">Verified Customer ✓</span>
                </div>
              </div>
            </div>
            <div className="review-card">
              <div className="review-stars" style={{ display: 'flex', gap: '3px', color: '#f59e0b', marginBottom: '12px' }}>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="#f59e0b" color="#f59e0b" />
                ))}
              </div>
              <p className="review-text">"Customer support is world-class. Orders arrive right on time every single day. Highly recommend to everyone in Bochum!"</p>
              <div className="review-author">
                <div className="review-avatar">ZM</div>
                <div className="review-meta">
                  <span className="author-name">Zoya Mirza</span>
                  <span className="author-status">Verified Customer ✓</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="info-section">
          <h2 className="info-title">{t("discoverFoodTitle") || "Discover Delicious Food Delights Online Near You"}</h2>
          <p className="info-text">{t("discoverFoodText") || "Craving something delicious? Discover a world of culinary delights right at your fingertips with Waseeny. Whether you're in the mood for hot cheesy pizza, fresh sushi, or a hearty burger, find it all delivered to your door fast."}</p>
          <button type="button" className="btn-see-more" onClick={() => navigate('/about-us')}>
            {t("seeMore") || "See More"} →
          </button>
        </div>
      </main>

      {/* ========== APP DOWNLOAD SECTION ==========
      <section className="app-download-section">
        <div className="app-download-content">
          <div className="app-download-text">
            <h2>{t("forABetterExperience")}</h2>
            <h3>{t("downloadTheWaseenyApp")}</h3>
            <p>{t("getYourFoodDelivered")}</p>
            <div className="app-download-buttons">
              <button className="app-store-btn google-play"></button>
              <button className="app-store-btn apple-store"></button>
            </div>
          </div>
        </div>
      </section> */}

      <footer className="home-footer">

        <div className="footer-content">

          <div className="footer-left">
            <div className="footer-logo">
              <img src={footerLogo} alt="Waseeny" className="footer-logo-image" />
            </div>
            <h3 className="footer-cta-title">
              {t("getApp")}</h3>
            <p className="footer-cta-text">
              {t("bestOffers")}
            </p>
          </div>


          <div className="footer-right">
            <img src={qrCode} alt="Scan to Download" className="qr-code-image" />
          </div>
        </div>


        <div className="footer-bottom">
          <p className="footer-copyright">(c) 2026 Waseeny. {t("allRightsReserved")}</p>
          <div className="footer-links">
            <button type="button" className="footer-link" onClick={() => navigate('/about-us')}>About Us</button>
            <button type="button" className="footer-link" onClick={() => navigate('/privacy-policy')}>{t("privacy")}</button>
            <button type="button" className="footer-link" onClick={() => navigate('/terms')}>{t("terms")}</button>
            <button type="button" className="footer-link" onClick={() => navigate('/contact-support')}>{t("contact")}</button>
          </div>
        </div>
      </footer>
      <RestaurantFiltersModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        initialFilters={filterState}
        onApply={(newFilters) => setFilterState(newFilters)}
      />
      <LocationPickerModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        initialCenter={mapCenter}
        showSavedAddresses
        onConfirm={async (data) => {
          await updateLocationData(data.lat, data.lng, data.address, data);
          setIsLocationModalOpen(false);
        }}
      />
    </div>
  );
};

export default Home;